import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { MessageBridgeService } from './message-bridge';

// ResizeObserver only fires when document.documentElement's own layout box changes size.
// document.body.scrollHeight can grow independently of that (out-of-flow content such as
// position: absolute/fixed overlays, or the tail end of a CSS height transition), leaving the
// parent iframe a few px short with no further observer callback to correct it. This poll is a
// cause-agnostic safety net that catches any such drift, deduped so it only posts on real change.
const DRIFT_POLL_INTERVAL_MS = 500;

// document.body.scrollHeight is always an integer, but the true rendered height is often
// fractional (sub-pixel line-heights, flex/grid gap math, non-100% zoom/DPI scaling) and
// browsers don't consistently round that fraction the same way. A small fixed buffer biases
// us toward over-reporting rather than risking a 1px shortfall that shows up as a real scrollbar.
const HEIGHT_SAFETY_BUFFER_PX = 2;

@Injectable({ providedIn: 'root' })
export class IframeResizeService implements OnDestroy {
  private ro?: ResizeObserver;
  private pollId?: ReturnType<typeof setInterval>;
  private lastHeight = -1;

  private readonly bridge = inject(MessageBridgeService);
  private readonly zone = inject(NgZone);

  init() {
    this.zone.runOutsideAngular(() => {
      const postHeight = () => {
        const height = document.body.scrollHeight;
        if (height === this.lastHeight) return;
        this.lastHeight = height;
        this.bridge.postMessage('IFRAME_HEIGHT', { height: height + HEIGHT_SAFETY_BUFFER_PX });
      };

      this.ro = new ResizeObserver(postHeight);
      this.ro.observe(document.documentElement);

      window.addEventListener('load', postHeight);
      this.pollId = setInterval(postHeight, DRIFT_POLL_INTERVAL_MS);
      postHeight();
    });
  }

  ngOnDestroy() {
    this.ro?.disconnect();
    clearInterval(this.pollId);
  }
}
